import React, { createContext, useContext, useEffect, useState } from 'react';
import { WrittenDirective } from '../../../../_types/awb';
import useWrittenDirectives from '../../../../_data_sources/WrittenDirectives/useWrittenDirectives';
import { Jurisdiction } from 'types/jurisdiction';
import useJurisdictions from 'data_sources/useJurisdictions';
import useIssuedManualSearch from './_data_sources/useIssuedManualSearch';
import { ManualSearchResult } from 'views/App/Portal/Policy/_types/search';
import useAccreditationVersion from '../../../../_data_sources/AccreditationVersions/useAccreditationVersion';

interface WrittenDirectivesContextType {
  writtenDirectives: WrittenDirective[] | undefined;
  isLoading: boolean;
  jurisdiction: Jurisdiction | undefined;
  searchQuery: ReturnType<typeof useIssuedManualSearch>;
  selectedItem: ManualSearchResult | null;
  setSelectedItem: (item: ManualSearchResult | null) => void;
  isContentLoading: boolean;
  setIsContentLoading: (value: boolean) => void;
  searchInputValue: string;
  setSearchInputValue: (value: string) => void;
}

const WrittenDirectivesContext = createContext<WrittenDirectivesContextType | undefined>(undefined);

export const useWrittenDirectivesContext = () => {
  const context = useContext(WrittenDirectivesContext);
  if (!context) {
    throw new Error('useWrittenDirectivesContext must be used within a WrittenDirectivesProvider');
  }
  return context;
};

interface WrittenDirectivesProviderProps {
  children: React.ReactNode;
  accreditingBodyId: string;
  versionId: string;
  standardId: string;
  jurisdictionId: string;
  disabled?: boolean;
}

export const WrittenDirectivesProvider: React.FC<WrittenDirectivesProviderProps> = ({
  children,
  accreditingBodyId,
  versionId,
  standardId,
  jurisdictionId,
  disabled = false,
}) => {
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction | undefined>(undefined);
  const [selectedItem, setSelectedItem] = useState<ManualSearchResult | null>(null);
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const jurisdictionQuery = useJurisdictions();
  const { data: version } = useAccreditationVersion(accreditingBodyId, versionId);

  useEffect(() => {
    if (jurisdictionQuery.data) {
      setJurisdiction(jurisdictionQuery.data.find((j: Jurisdiction) => j.id === jurisdictionId));
    }
  }, [jurisdictionQuery.data, jurisdictionId]);

  const { data: writtenDirectives, isLoading: isDirectivesLoading } = useWrittenDirectives(
    accreditingBodyId,
    versionId,
    standardId,
    jurisdictionId
  );

  const searchQuery = useIssuedManualSearch<ManualSearchResult>(
    searchInputValue,
    {
      jurisdictionId,
      verticalId: version?.verticalId,
    },
    {
      enabled: searchInputValue.length > 0 && !!version,
    }
  );

  const value = {
    writtenDirectives,
    isLoading: isDirectivesLoading,
    jurisdiction,
    searchQuery,
    selectedItem,
    setSelectedItem,
    isContentLoading,
    setIsContentLoading,
    searchInputValue,
    setSearchInputValue,
  };

  return <WrittenDirectivesContext.Provider value={value}>{children}</WrittenDirectivesContext.Provider>;
};
